<template>
    <main class="static-page max-width-container">
        <h1>Privacy policy</h1>
        <p class="last-updated">Last updated: {{ lastUpdated }}</p>

        <h2>Who runs this</h2>
        <p>
            Tripwards is a hobby project run by Žiga Stopinšek and Lea Opačić
            from Ljubljana, Slovenia. It's not a company and it doesn't make any
            money.
        </p>
        <p>
            Tripwards is made for travelers, explorers, and tourists. We only
            process the data you share, and we don't generate data implicitly
            from your usage. You need to be at least 13 to use it. If you're
            under the age of digital consent where you live (13 to 16), please
            ask a parent first.
        </p>
        <p>
            We don't publish an email address, but if you have a question about
            your data or want to use any of the rights below, send us a message
            through the
            <router-link to="/contact">contact form</router-link>. It lands
            straight in our inbox and we answer by email.
        </p>

        <h2>What we store</h2>
        <ul>
            <li>
                Your account. To create one we need your name, email and a
                password. If you sign in with Google, we get your name, email
                and avatar from Google instead of a password.
            </li>
            <li>Your home city, for personalizing your statistics.</li>
            <li>
                Whatever you enter about your trips: locations, dates, notes,
                expenses, and the names of people you travelled with. Those
                names are only used for your own statistics, so if you'd rather
                not, use first names or nicknames.
            </li>
            <li>Bucket lists, saved trip ideas and your travel schedule.</li>
            <li>Photos you upload to a trip, location or bucket-list item.</li>
            <li>
                Files you upload to import trips or bucket-list items. We keep
                them so you can check what went wrong if an import fails.
            </li>
        </ul>
        <p>Apart from the account details, it's all up to you what you add.</p>

        <h2>Why, and on what legal basis</h2>
        <p>
            Most of it is just the app doing its job: you can't keep a travel
            log without it storing your trips. The legal basis for that is that
            it's necessary to provide the service you signed up for (performing
            a contract with you). The same goes for the standard map, which is
            part of the service. It comes from OpenStreetMap and loads for
            everyone who uses Tripwards.
        </p>
        <p>
            Some processing keeps the site secure and lets us fix it when it
            breaks: rate-limiting login attempts, and server logs. That's based
            on our legitimate interest in running a site that can't easily be
            abused and that we can debug.
        </p>
        <p>
            Messages you send through the contact form are only used to answer
            you. The basis is our legitimate interest in replying, or steps you
            asked for before signing up.
        </p>
        <p>
            Photos from Pexels and Wikipedia and the satellite map only load if
            you agree to it in the cookie banner, so that part is based on your
            consent. The
            <router-link to="/cookies">cookie page</router-link> has the
            details.
        </p>

        <h2>Who else handles it</h2>
        <p>Hosting and file storage are both in the EU. Apart from that:</p>
        <ul>
            <li>Resend sends account and notification emails.</li>
            <li>Google, if you register or sign in with a Google account.</li>
            <li>
                Cloudflare Turnstile checks that you're not a bot when you
                register or use the contact form while logged out. It looks at
                signals from your browser and sends your IP address to
                Cloudflare. It doesn't set cookies.
            </li>
            <li>
                OpenStreetMap, which provides the standard map. Your browser
                loads the map tiles straight from the OpenStreetMap Foundation,
                so it sees your IP address. It doesn't set cookies.
            </li>
            <li>
                Google Gemini, but only when you use an AI feature (generating
                trip or bucket-list ideas). It gets your home city and country,
                your bucket lists and their items, your past trips with their
                years and locations, your ratings, the trip ideas you've saved,
                and the travel style and season you picked. That includes any
                names you typed for trips, lists and items, so keep anything
                private out of those. It doesn't get your account name, email,
                notes, expenses or recorded travel companions.
            </li>
            <li>
                Pexels, Wikipedia and MapTiler, once you've allowed external
                content in the cookie banner. From then on your browser loads
                images and satellite map tiles directly from them.
            </li>
        </ul>
        <p>
            Google, Cloudflare, OpenStreetMap, Pexels, Wikipedia and MapTiler
            each decide for themselves what they do with data that reaches them,
            under their own privacy policies.
        </p>
        <p>
            When the site looks something up on Nominatim, Wikipedia or Pexels
            for you (say, while you're adding a location), the request comes
            from our server, not your browser. It carries your search text, but
            not your IP address or any account details.
        </p>

        <h2>Other people on Tripwards</h2>
        <p>
            Friends you add can see your name and how many trips and bucket
            lists you have. If you share a trip, bucket list, trip idea or plan
            and the other person accepts, they get their own copy, which stays
            in their account even if you later delete yours. We email them a
            short notice with your name and the name of what you shared.
        </p>

        <h2>Data leaving the EU</h2>
        <p>
            Resend, Google and Cloudflare are based in the US. These transfers
            rely on the EU–US Data Privacy Framework or, where that doesn't
            apply, on EU Standard Contractual Clauses. The OpenStreetMap
            Foundation is in the UK, which the EU treats as providing adequate
            protection. If you allow external content, your browser also talks
            directly to Pexels, Wikipedia and MapTiler, some of which are
            outside the EU. That happens because you chose it.
        </p>

        <h2>How long it's kept</h2>
        <ul>
            <li>
                Your account and everything in it: until you ask us to delete
                it.
            </li>
            <li>
                Import files: we clear out the ones older than 30 days when you
                open your imports list, so if you never do, they stay until your
                account is deleted.
            </li>
            <li>
                Cached results from Nominatim, Wikipedia and Pexels: up to 30
                days. We also keep a short list of the latest failed lookups,
                search text included, until newer ones replace them.
            </li>
            <li>
                Contact form messages: until we've dealt with them, and 12
                months at most.
            </li>
            <li>
                Web server and app logs: 14 days. They include IP addresses and
                are kept for security and debugging.
            </li>
        </ul>

        <h2>Deleting your data</h2>
        <p>
            There's no delete button yet. To get your account deleted, send us a
            message through the
            <router-link to="/contact?topic=deletion">contact form</router-link>
            and we'll remove it by hand, including the photos you uploaded. If
            you delete a trip or photo yourself in the app, the image file can
            stay in our storage until we clean it up. Ask us if you want it gone
            right away.
        </p>

        <h2>Your rights</h2>
        <p>
            You can ask us to show you the data we hold about you, correct it,
            delete it, or send you a copy. You can also object to how we use it,
            or withdraw consent you've given (for external content, say) at any
            time. Just use the
            <router-link to="/contact?topic=data_access"
                >contact form</router-link
            >. To make sure it's you, we'll reply to the email address on your
            account, and we answer within one month. Trips and bucket lists you
            can also export yourself, any time, from their own pages. If you
            think we've mishandled your data, you can complain to the data
            protection authority in your country.
        </p>

        <h2>What we don't do</h2>
        <p>
            We don't sell your data, and there are no ads and no analytics. The
            services listed above handle some of your data under their own
            privacy policies. The few cookies the site does set are listed on
            the <router-link to="/cookies">cookie page</router-link>.
        </p>

        <h2>Changes</h2>
        <p>If we change this policy, the date at the top changes too.</p>
    </main>
</template>

<script>
export default {
    name: "Privacy",
    data() {
        return {
            lastUpdated: "October 4, 2026",
        };
    },
};
</script>
